
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { AdjustmentsIcon, ArrowNarrowRightIcon, DownloadIcon, PaperClipIcon, PauseIcon, PlayIcon, PlusIcon, VolumeUpIcon, } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { FormEvent, useRef, useState } from 'react';
import styles from '@/components/audio-generator/AudioLanding.module.css';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import { AUDIO_GENERATOR_COST, AUDIO_GENERATOR_DRAFT_KEY, AUDIO_GENERATOR_MAX_PROMPT, AUDIO_PROMPT_STARTERS, } from '@/constants/audioGenerator';
import { SOUND_EFFECT_EXAMPLES } from '@/constants/soundEffectExamples';
import { useAnalytics } from '@/contexts/Analytics';
import { useUser } from '@/utils/useUser';
const Footer = dynamic(() => import('@/components/layout/Footer'));
const modeDescriptions = [
    'Voiceovers with feeling.',
    'An original mood for your edit.',
    'Characters with something to say.',
    'Scenes you can step inside.',
] as const;
const questions = [
    [
        'What can I create with Seed Audio 1.0?',
        'Create narration, dialogue, music, sound effects, or a scene that combines them. Describe the voices, mood, setting, and how the audio should unfold. Seed Audio 1.0 is ByteDance’s audio creation model.',
    ],
    [
        'Can I use a reference voice or image?',
        'Yes. Attach up to three MP3 or WAV audio clips, each up to 30 seconds, or one JPG, PNG, or WebP image. Each file can be up to 10 MB. Use audio references and image guidance separately. Only upload media you have permission to use.',
    ],
    [
        'Can I choose the length and download format?',
        'Set a target duration up to 120 seconds, or choose Auto. Duration is prompt guidance, so the actual length may vary. Download MP3, WAV, OGG Opus, or raw PCM. PCM includes a WAV preview for listening.',
    ],
    [
        'How much does it cost to generate audio?',
        'Each successful generation uses {{count}} characters from your plan’s audio allowance. You can see your balance and the cost before generating. Listening to the demos on this page does not use your allowance.',
    ],
    [
        'How is this different from text-to-speech?',
        'Text-to-speech focuses on reading a script in a selected voice. This generator lets you describe a whole audio idea, including voices, music, ambience, and effects. Use a prompt to direct the performance and scene.',
    ],
] as const;
function clock(seconds: number) {
    const rounded = Math.max(0, Math.floor(seconds));
    return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`;
}
export default function AIAudioGeneratorLanding() {
    const { t, lang } = useLocale('audio-generator-landing');
    const { user } = useUser();
    const router = useRouter();
    const analytics = useAnalytics();
    const [mode, setMode] = useState(0);
    const [prompt, setPrompt] = useState<string | null>(null);
    const [duration, setDuration] = useState<number | null>(null);
    const [sampleIndex, setSampleIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [playError, setPlayError] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [sampleDuration, setSampleDuration] = useState<number | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const promptRef = useRef<HTMLTextAreaElement>(null);
    const playAttempt = useRef(0);
    const sample = SOUND_EFFECT_EXAMPLES[sampleIndex];
    const starter = AUDIO_PROMPT_STARTERS[mode];
    const currentPrompt = prompt ?? t<string>(starter.prompt, {}, { ns: 'audio-generator' });
    // Explicit digits keep server and browser ICU defaults from disagreeing.
    const cost = new Intl.NumberFormat(new Intl.Locale(lang, {
        numberingSystem: lang === 'ar' ? 'arab' : 'latn',
    }).toString()).format(AUDIO_GENERATOR_COST);
    const destination = user
        ? '/audio-generator'
        : '/login?preset=audio-generator#auth-sign-up';
    const seconds = sampleDuration ?? sample.duration;
    const progress = Math.min(1, currentTime / seconds);
    function openGenerator(value: string, target: number | null, source: string) {
        try {
            localStorage.setItem(AUDIO_GENERATOR_DRAFT_KEY, JSON.stringify({ prompt: value.trim(), duration: target }));
        }
        catch {
            /* Opening the tool still works when browser storage is disabled. */
        }
        analytics.track('Audio Generator Landing CTA Clicked', {
            source,
            mode: starter.kind,
        });
        void router.push(destination);
    }
    function submit(event: FormEvent) {
        event.preventDefault();
        openGenerator(currentPrompt, duration ?? starter.duration, 'prompt');
    }
    function useSample() {
        setPrompt(t(sample.prompt, {}, { ns: 'sound-effects' }));
        setDuration(sample.duration);
        setMode(3);
        promptRef.current?.focus({ preventScroll: true });
        document.getElementById('create-audio')?.scrollIntoView({
            block: 'start',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
        });
    }
    async function togglePlayback() {
        const audio = audioRef.current;
        if (!audio || loadingAudio)
            return;
        if (!audio.paused) {
            audio.pause();
            return;
        }
        const attempt = ++playAttempt.current;
        setLoadingAudio(true);
        setPlayError(false);
        try {
            if (audio.ended)
                audio.currentTime = 0;
            await audio.play();
        }
        catch {
            if (attempt === playAttempt.current)
                setPlayError(true);
        }
        finally {
            if (attempt === playAttempt.current)
                setLoadingAudio(false);
        }
    }
    function chooseSample(index: number) {
        if (index === sampleIndex)
            return;
        playAttempt.current++;
        audioRef.current?.pause();
        setPlaying(false);
        setLoadingAudio(false);
        setPlayError(false);
        setCurrentTime(0);
        setSampleDuration(null);
        setSampleIndex(index);
    }
    const schema = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebApplication',
                name: t('AI Audio Generator'),
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                url: `https://easy-peasy.ai${lang === 'en' ? '' : `/${lang}`}/ai-audio-generator`,
                description: t('Create voices, music, dialogue, and soundscapes with Seed Audio 1.0. Guide your audio with references and download it in the format your project needs.'),
            },
            {
                '@type': 'FAQPage',
                mainEntity: questions.map(([question, answer]) => ({
                    '@type': 'Question',
                    name: t(question),
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: t(answer, { count: cost }),
                    },
                })),
            },
        ],
    };
    return (<div className={styles.page} dir={lang === 'ar' ? 'rtl' : undefined}>
      <Seo templateTitle={t('AI Audio Generator — Voices, Music & Sound with Seed Audio 1.0')} description={t('Create voices, music, dialogue, and soundscapes with Seed Audio 1.0. Guide your audio with references and download it in the format your project needs.')} image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(t('AI Audio Generator'))}`} structuredData={false}/>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
        }}/>
      </Head>
      <LandingPageNavigation />
      <main>
        <section className={`${styles.container} ${styles.hero}`}>
          <div className={styles.heroCopy}>
            <div className={styles.eyebrow}>
              {t('AI Audio Generator')}
              <i aria-hidden='true'/>
              <span>Seed Audio 1.0</span>
            </div>
            <h1>
              {t('Every idea')}
              <em>{t('has a sound.')}</em>
            </h1>
            <p className={styles.intro}>
              {t('A voice that tells your story. Music that sets the mood. A scene that feels alive. Create it all from a few words.')}
            </p>
            <div className={styles.actions}>
              <Link href={destination} className={styles.primary}>
                {t('Create your audio')}
                <ArrowNarrowRightIcon aria-hidden='true'/>
              </Link>
              <a href='#create-audio' className={styles.secondary}>
                {t('Find your starting point')}
                <ArrowNarrowRightIcon aria-hidden='true'/>
              </a>
            </div>
            <p className={styles.heroNote}>
              {t('Powered by ByteDance · {{count}} audio characters per generation', { count: cost })}
            </p>
          </div>
          <div>
            <div className={styles.deck}>
              <div className={styles.deckTop}>
                <span>{t('The listening room')}</span>
                <strong>{t('Real Seed Audio samples')}</strong>
              </div>
              <div className={styles.deckBody}>
                <span className={styles.sampleCategory}>
                  {t(sample.category, {}, { ns: 'sound-effects' })} · MP3
                </span>
                <h2>{t(sample.name, {}, { ns: 'sound-effects' })}</h2>
                <svg className={styles.wave} viewBox='0 0 640 120' preserveAspectRatio='none' aria-hidden='true'>
                  <line x1='0' x2='640' y1='60' y2='60' stroke='#334155' strokeWidth='1'/>
                  {sample.peaks.map((peak, index) => {
            const height = Math.max(3, peak * 1.04);
            return (<rect key={index} x={index * 8 + 1} y={(120 - height) / 2} width='4' height={height} rx='2' fill={index / sample.peaks.length < progress
                    ? 'var(--color-primary-300)'
                    : '#64748b'}/>);
        })}
                </svg>
                <div className={styles.transport} dir='ltr'>
                  <button type='button' className={styles.play} disabled={loadingAudio} onClick={togglePlayback} aria-label={playing
            ? t('Pause {{name}}', {
                name: t(sample.name, {}, { ns: 'sound-effects' }),
            })
            : t('Play {{name}}', {
                name: t(sample.name, {}, { ns: 'sound-effects' }),
            })} aria-busy={loadingAudio}>
                    {playing ? (<PauseIcon aria-hidden='true'/>) : (<PlayIcon aria-hidden='true'/>)}
                  </button>
                  <input type='range' min={0} max={seconds} step={0.01} value={currentTime} disabled={sampleDuration === null} aria-label={t('Seek in the sample')} aria-valuetext={`${clock(currentTime)} / ${clock(seconds)}`} onChange={(event) => {
            const audio = audioRef.current;
            if (audio?.readyState) {
                audio.currentTime = Number(event.target.value);
                setCurrentTime(audio.currentTime);
            }
        }}/>
                  <output aria-live='off'>
                    {clock(currentTime)} / {clock(seconds)}
                  </output>
                </div>
                <p className={styles.samplePrompt}>
                  {t(sample.prompt, {}, { ns: 'sound-effects' })}
                </p>
                <button type='button' className={styles.sampleUse} onClick={useSample}>
                  {t('Make this prompt your own')}
                  <ArrowNarrowRightIcon aria-hidden='true'/>
                </button>
                {playError && (<p role='alert' className={styles.playError}>
                    {t('Could not play this sample. Please try again.')}
                  </p>)}
              </div>
              <div className={styles.sampleChoices} aria-label={t('Choose a sample')}>
                {SOUND_EFFECT_EXAMPLES.map((example, index) => (<button type='button' key={example.id} aria-pressed={sampleIndex === index} onClick={() => chooseSample(index)}>
                    <VolumeUpIcon aria-hidden='true'/>
                    {t(example.category, {}, { ns: 'sound-effects' })}
                  </button>))}
              </div>
              <audio key={sample.id} ref={audioRef} src={sample.url} preload='none' onPlay={() => {
            setPlaying(true);
            analytics.track('Audio Generator Demo Played', {
                sample: sample.id,
            });
        }} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)} onLoadedMetadata={(event) => {
            if (Number.isFinite(event.currentTarget.duration))
                setSampleDuration(event.currentTarget.duration);
        }} onError={() => {
            setPlaying(false);
            setLoadingAudio(false);
            setPlayError(true);
        }}/>
            </div>
            <p className={styles.sampleNote}>
              {t('A taste of its sound effects. Your next creation can include voices and music, too.')}
            </p>
          </div>
        </section>

        <div className={styles.possibilities}>
          <div className={`${styles.container} ${styles.possibilitiesInner}`}>
            <span>{t('One model. So many possibilities.')}</span>
            <strong>{t('Voiceovers')}</strong>
            <strong>{t('Music')}</strong>
            <strong>{t('Dialogue')}</strong>
            <strong>{t('Soundscapes')}</strong>
          </div>
        </div>

        <section className={`${styles.container} ${styles.create}`} id='create-audio'>
          <div className={styles.sectionHeader}>
            <div>
              <div className={styles.eyebrow}>{t('Start with an idea')}</div>
              <h2>{t('What are we making?')}</h2>
            </div>
            <p>
              {t('Pick a starting point, change a few words, and take your idea into the studio.')}
            </p>
          </div>
          <div className={styles.promptWorkspace}>
            <div className={styles.modes} aria-label={t('Choose a starting point')}>
              {AUDIO_PROMPT_STARTERS.map((item, index) => (<button className={styles.mode} key={item.kind} type='button' aria-pressed={mode === index} onClick={() => {
                setMode(index);
                setPrompt(null);
                setDuration(null);
            }}>
                  <span aria-hidden='true'>0{index + 1}</span>
                  <span>
                    <strong>
                      {t(item.kind, {}, { ns: 'audio-generator' })}
                    </strong>
                    <small>{t(modeDescriptions[index])}</small>
                  </span>
                  <ArrowNarrowRightIcon aria-hidden='true'/>
                </button>))}
            </div>
            <form className={styles.promptForm} onSubmit={submit}>
              <div className={styles.promptTop}>
                <label htmlFor='audio-landing-prompt'>
                  {t('Your creative brief')}
                </label>
                <span>{t('Make it your own')}</span>
              </div>
              <textarea id='audio-landing-prompt' ref={promptRef} rows={5} value={currentPrompt} maxLength={AUDIO_GENERATOR_MAX_PROMPT} onChange={(event) => setPrompt(event.target.value)} required aria-describedby='audio-landing-prompt-help'/>
              <div className={styles.promptCount}>
                <span id='audio-landing-prompt-help'>
                  {t('Add a mood, a setting, and the details that matter.')}
                </span>
                <span>
                  {currentPrompt.length} / {AUDIO_GENERATOR_MAX_PROMPT}
                </span>
              </div>
              <div className={styles.promptBottom}>
                <small>
                  {t('Your prompt comes with you. Add references and adjust settings in the studio.')}
                </small>
                <button type='submit' className={styles.primary} disabled={!currentPrompt.trim()}>
                  {t('Continue with this prompt')}
                  <ArrowNarrowRightIcon aria-hidden='true'/>
                </button>
              </div>
            </form>
          </div>
        </section>

        <div className={styles.directionWrap}>
          <section className={`${styles.container} ${styles.direction}`}>
            <div>
              <div className={styles.eyebrow}>
                {t('You direct. Seed creates.')}
              </div>
              <h2>
                {t('A little direction.')}
                <br />
                {t('A lot of range.')}
              </h2>
              <p>
                {t('Give the model a feeling to follow, a voice to reference, or a scene to bring to life. Keep shaping until it fits your project.')}
              </p>
              <div className={styles.referenceNote}>
                <PaperClipIcon aria-hidden='true'/>
                <strong>@Audio1</strong>
                <span>{t('Your reference. Your direction.')}</span>
              </div>
            </div>
            <div>
              <article className={styles.capability}>
                <PaperClipIcon aria-hidden='true'/>
                <div>
                  <h3>{t('Bring a reference')}</h3>
                  <p>
                    {t('Use up to three audio clips to guide a voice or style. Or add an image and describe the sounds of the scene.')}
                  </p>
                </div>
              </article>
              <article className={styles.capability}>
                <AdjustmentsIcon aria-hidden='true'/>
                <div>
                  <h3>{t('Fine-tune the delivery')}</h3>
                  <p>
                    {t('Adjust speed, volume, and pitch. Choose a target length and a sample rate from 8 to 48 kHz. The actual duration may vary.')}
                  </p>
                </div>
              </article>
              <article className={styles.capability}>
                <DownloadIcon aria-hidden='true'/>
                <div>
                  <h3>{t('Take it into your project')}</h3>
                  <p>
                    {t('Listen, save, and download. Your creations stay in your history, ready for the next edit, episode, or idea.')}
                  </p>
                  <div className={styles.formatList}>
                    <span>MP3</span>
                    <span>WAV</span>
                    <span>OGG Opus</span>
                    <span>PCM</span>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </div>

        <section className={`${styles.container} ${styles.faq}`}>
          <div>
            <div className={styles.eyebrow}>{t('Before you press play')}</div>
            <h2>{t('A few useful answers.')}</h2>
          </div>
          <div className={styles.questions}>
            {questions.map(([question, answer]) => (<details key={question}>
                <summary>
                  {t(question)}
                  <PlusIcon aria-hidden='true'/>
                </summary>
                <p>{t(answer, { count: cost })}</p>
              </details>))}
          </div>
        </section>

        <section className={`${styles.container} ${styles.final}`}>
          <div>
            <h2>
              {t('Press play on')}
              <br />
              <em>{t('your next idea.')}</em>
            </h2>
            <div className={styles.related}>
              <Link href='/text-to-speech'>
                {t('Explore text-to-speech')} ↗
              </Link>
              <Link href='/ai-sound-effects-generator'>
                {t('Explore sound effects')} ↗
              </Link>
            </div>
          </div>
          <Link href={destination} className={styles.primary}>
            {t('Create your audio')}
            <ArrowNarrowRightIcon aria-hidden='true'/>
          </Link>
        </section>
      </main>
      <Footer />
    </div>);
}

    async function __Next_Translate__getStaticProps__1a13a29a114__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-audio-generator',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a13a29a114__ as getStaticProps }
  